// Simple scroll lock utility with reference counting.
// Stores previous body/document styles on dataset when first lock is applied,
// increments a counter for nested locks, and restores styles only when the
// last lock is released.

export function lockScroll() {
  try {
    const body = document.body;
    const doc = document.documentElement;
    const cur = parseInt(body.dataset.scrollLockCount || '0', 10) || 0;
    if (cur === 0) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      body.dataset.prevScrollY = String(scrollY);
      body.dataset.prevOverflow = body.style.overflow || '';
      body.dataset.prevDocOverflow = doc.style.overflow || '';
      body.dataset.prevPosition = body.style.position || '';
      body.dataset.prevTop = body.style.top || '';
      body.dataset.prevLeft = body.style.left || '';
      body.dataset.prevRight = body.style.right || '';
      body.dataset.prevWidth = body.style.width || '';
      body.dataset.prevTouch = body.style.touchAction || '';

      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      doc.style.overflow = 'hidden';
      body.style.touchAction = 'none';
    }
    body.dataset.scrollLockCount = String(cur + 1);
  } catch (err) {
    void err;
    // Best-effort; swallow errors
    try { document.body.style.overflow = 'hidden'; } catch { void 0; }
  }
}

export function unlockScroll() {
  try {
    const body = document.body;
    const doc = document.documentElement;
    const cur = parseInt(body.dataset.scrollLockCount || '0', 10) || 0;
    // Even if the count is missing or 0 (inconsistent state), attempt to restore
    const next = Math.max(0, cur - 1);
    if (next === 0) {
      const prevOverflow = body.dataset.prevOverflow || '';
      const prevDocOverflow = body.dataset.prevDocOverflow || '';
      const prevPosition = body.dataset.prevPosition || '';
      const prevTop = body.dataset.prevTop || '';
      const prevLeft = body.dataset.prevLeft || '';
      const prevRight = body.dataset.prevRight || '';
      const prevWidth = body.dataset.prevWidth || '';
      const prevTouch = body.dataset.prevTouch || '';
      const prevScrollY = body.dataset.prevScrollY || '0';


      doc.style.overflow = prevDocOverflow;
      body.style.position = prevPosition;
      body.style.top = prevTop;
      body.style.left = prevLeft;
      body.style.right = prevRight;
      body.style.width = prevWidth;
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouch;

      // remove stored data attributes
      delete body.dataset.prevOverflow;
      delete body.dataset.prevDocOverflow;
      delete body.dataset.prevPosition;
      delete body.dataset.prevTop;
      delete body.dataset.prevLeft;
      delete body.dataset.prevRight;
      delete body.dataset.prevWidth;
      delete body.dataset.prevTouch;
      delete body.dataset.prevScrollY;

      // restore scroll position after styles have been reset; use RAF+timeout
      try {
        const parsed = parseInt(prevScrollY, 10) || 0;
        const maxY = Math.max(0, (document.documentElement?.scrollHeight || 0) - window.innerHeight);
        const y = Math.min(parsed, maxY);
        try { window.scrollTo(0, y); } catch { void 0; }
        try {
          requestAnimationFrame(() => {
            try { window.scrollTo(0, y); } catch (_e2) { void _e2; }
          });
        } catch { void 0; }
        try {
          // Keep two retries — immediate and one delayed — to handle
          // small reflows without causing the page to visibly jitter.
          setTimeout(() => { try { window.scrollTo(0, y); } catch (_e3) { void _e3; } }, 50);
          setTimeout(() => { try { window.scrollTo(0, y); } catch (_e4) { void _e4; } }, 200);

          // If the page still ends up at 0 (e.g. content removed after unlock),
          // attempt a couple of longer retries to restore the original position.
          if ((parseInt(prevScrollY, 10) || 0) > 0) {
            setTimeout(() => {
              try {
                if ((window.scrollY || window.pageYOffset || 0) === 0) {
                  const maxY2 = Math.max(0, (document.documentElement?.scrollHeight || 0) - window.innerHeight);
                  const y2 = Math.min(parseInt(prevScrollY, 10) || 0, maxY2);
                  try { window.scrollTo(0, y2); } catch (_e5) { void _e5; }
                }
              } catch { void 0; }
            }, 350);

            setTimeout(() => {
              try {
                if ((window.scrollY || window.pageYOffset || 0) === 0) {
                  const maxY3 = Math.max(0, (document.documentElement?.scrollHeight || 0) - window.innerHeight);
                  const y3 = Math.min(parseInt(prevScrollY, 10) || 0, maxY3);
                  try { window.scrollTo(0, y3); } catch (_e6) { void _e6; }
                }
              } catch { void 0; }
            }, 700);
          }
        } catch { void 0; }
      } catch { void 0; }
    }
    if (next > 0) {
      body.dataset.scrollLockCount = String(next);
    } else {
      delete body.dataset.scrollLockCount;
    }
  } catch (err) {
    void err;
    try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { void 0; }
  } finally {
    // As a final safety, ensure body/document styles are not left in locked state
    try {
      const body = document.body;
      const doc = document.documentElement;
      // If no lock remains, clear any leftover styles that prevent scrolling
      const remaining = parseInt(body.dataset.scrollLockCount || '0', 10) || 0;
      if (remaining === 0) {
        try { body.style.position = body.style.position || ''; } catch { void 0; };
        try { body.style.top = body.style.top || ''; } catch { void 0; };
        try { body.style.left = body.style.left || ''; } catch { void 0; };
        try { body.style.right = body.style.right || ''; } catch { void 0; };
        try { body.style.width = body.style.width || ''; } catch { void 0; };
        try { body.style.overflow = body.style.overflow || ''; } catch { void 0; };
        try { doc.style.overflow = doc.style.overflow || ''; } catch { void 0; };
        try { body.style.touchAction = body.style.touchAction || ''; } catch { void 0; };
      }
    } catch { void 0; }
  }
}

// Forcefully remove all locks and restore styles. Use only for recovery.
export function forceUnlockAll() {
  try {
    const body = document.body;
    const doc = document.documentElement;

    const prevOverflow = body.dataset.prevOverflow || '';
    const prevDocOverflow = body.dataset.prevDocOverflow || '';
    const prevPosition = body.dataset.prevPosition || '';
    const prevTop = body.dataset.prevTop || '';
    const prevLeft = body.dataset.prevLeft || '';
    const prevRight = body.dataset.prevRight || '';
    const prevWidth = body.dataset.prevWidth || '';
    const prevTouch = body.dataset.prevTouch || '';
    const prevScrollY = body.dataset.prevScrollY || '0';

    doc.style.overflow = prevDocOverflow;
    body.style.position = prevPosition;
    body.style.top = prevTop;
    body.style.left = prevLeft;
    body.style.right = prevRight;
    body.style.width = prevWidth;
    body.style.overflow = prevOverflow;
    body.style.touchAction = prevTouch;

    delete body.dataset.prevOverflow;
    delete body.dataset.prevDocOverflow;
    delete body.dataset.prevPosition;
    delete body.dataset.prevTop;
    delete body.dataset.prevLeft;
    delete body.dataset.prevRight;
    delete body.dataset.prevWidth;
    delete body.dataset.prevTouch;
    delete body.dataset.prevScrollY;
    delete body.dataset.scrollLockCount;

    // restore scroll position
    try {
      const parsed = parseInt(prevScrollY, 10) || 0;
      const maxY = Math.max(0, (document.documentElement?.scrollHeight || 0) - window.innerHeight);
      const y = Math.min(parsed, maxY);
      try { window.scrollTo(0, y); } catch { void 0; }
      try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch (_e2) { void _e2; } }); } catch { void 0; }
      try { setTimeout(() => { try { window.scrollTo(0, y); } catch (_e3) { void _e3; } }, 50); setTimeout(() => { try { window.scrollTo(0, y); } catch (_e4) { void _e4; } }, 200); } catch { void 0; }
    } catch { void 0; }
    } catch (err) {
    void err;
    try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { void 0; }
  }
}
